import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const DraggableModal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  initialWidth = 800, 
  initialHeight = 600, 
  minWidth = 400, 
  minHeight = 300 
}) => {
  // Resizable modal state
  const [modalSize, setModalSize] = useState({ width: initialWidth, height: initialHeight });
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  
  // Draggable modal state
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Initialize modal position to center when opened
  useEffect(() => {
    if (isOpen) {
      const centerX = (window.innerWidth - modalSize.width) / 2;
      const centerY = (window.innerHeight - modalSize.height) / 2;
      setModalPosition({ x: Math.max(0, centerX), y: Math.max(0, centerY) });
    }
  }, [isOpen, modalSize.width, modalSize.height]);

  // Drag handlers
  const handleDragStart = (e) => {
    // Only allow dragging from specific areas (header, non-content areas)
    // Don't drag when clicking on interactive elements or content areas
    if (e.target.closest('.resize-handle') || 
        e.target.closest('button') || 
        e.target.closest('input') || 
        e.target.closest('select') || 
        e.target.closest('textarea') ||
        e.target.closest('.no-drag') ||
        e.target.closest('.modal-content') ||
        e.target.closest('pre') ||
        e.target.closest('code') ||
        // Allow text selection by checking if user is trying to select text
        (window.getSelection && window.getSelection().toString() !== '')) {
      return;
    }

    // Only allow dragging from header area
    if (!e.target.closest('.modal-header')) {
      return;
    }

    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    e.preventDefault();
  };

  // Resize handlers
  const handleResizeStart = (e) => {
    setIsResizing(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      width: modalSize.width,
      height: modalSize.height
    });
    e.preventDefault();
    e.stopPropagation();
  };

  // Mouse move and up handlers
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing) {
        const newWidth = Math.max(minWidth, dragStart.width + (e.clientX - dragStart.x));
        const newHeight = Math.max(minHeight, dragStart.height + (e.clientY - dragStart.y));
        setModalSize({ width: newWidth, height: newHeight });
      } else if (isDragging) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        // Keep modal within viewport bounds with minimum visible area
        const minVisibleHeight = 50; // Ensure at least header is visible
        const maxX = window.innerWidth - 100; // Keep 100px visible on right
        const maxY = window.innerHeight - minVisibleHeight;
        
        setModalPosition({
          x: Math.max(-modalSize.width + 100, Math.min(maxX, newX)),
          y: Math.max(0, Math.min(maxY, newY))
        });
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setIsDragging(false);
    };

    if (isResizing || isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, isDragging, dragStart, dragOffset, modalSize, minWidth, minHeight]);

  // ESC key handler
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div
        className="bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col relative"
        style={{
          width: modalSize.width,
          height: modalSize.height,
          transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
          position: 'absolute',
          top: 0,
          left: 0,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
        onMouseDown={handleDragStart}
      >
        {/* Header */}
        <div className="modal-header flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50 rounded-t-xl cursor-grab select-none">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors no-drag"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="modal-content flex-1 overflow-hidden">
          {children}
        </div>

        {/* Resize Handle */}
        <div 
          className="resize-handle absolute bottom-0 right-0 w-4 h-4 cursor-nw-resize bg-gray-300 hover:bg-gray-400 opacity-50 hover:opacity-100 transition-opacity"
          style={{
            clipPath: 'polygon(100% 0, 0 100%, 100% 100%)'
          }}
          onMouseDown={handleResizeStart}
          title="Drag to resize"
        />
      </div>
    </div>
  );
};

export default DraggableModal;